/**
 * Browser API-call + page-view logging (GO-1580 + GO-2952).
 *
 * Two concerns, two independently-toggled loggers under one service
 * (`platform-web`), facet by `@logger`:
 *
 * - **`web.fetch`** (`browser_request`) — one global `window.fetch`
 *   instrumentation, installed once at client bootstrap, logs every browser API
 *   call (one path, no double-logging with the tRPC links). Governed by
 *   `NEXT_PUBLIC_WRANGO_BROWSER_LOG_MODE = off | ids | full`
 *   (default: `ids` in prod, `full` otherwise):
 *     - `off`  — nothing.
 *     - `ids`  — a compact line per call: method/url/status + correlation/session id.
 *     - `full` — the above plus redacted request/response bodies.
 * - **`web.pageview`** (`page_view`) — one line per SPA navigation. Governed
 *   separately by `NEXT_PUBLIC_WRANGO_BROWSER_PAGEVIEW = true | false`, so you can
 *   keep lightweight nav tracking on while dialing fetch noise down (or vice versa).
 *
 * Redaction happens inside the shared logger (redactEntry over the whole line),
 * so Authorization/tokens never reach the console — even in `full`. It defaults
 * on everywhere except the local dev server (`NEXT_PUBLIC_WRANGO_BROWSER_LOG_REDACT`
 * overrides); see `browserRedactEnabled`.
 *
 * Console channel: prod strips `console.log` (next.config `removeConsole`), so
 * the sink uses `console.warn` in prod (survives + Jam captures it) and
 * `console.log` elsewhere.
 *
 * Browser-safe: imports the isomorphic logger/redactor by file, not the barrel.
 */

import { createLogger, type Logger, type LogSink } from "@/lib/logging/logger";
import { envBool } from "./config";
import { loggingDefaults } from "./logging-defaults";
import { getOrCreateSessionId } from "./session-id";

const CORRELATION_ID_HEADER = "x-correlation-id";
const MAX_BODY_CHARS = 10_000;

/**
 * Datadog site (matches datadog-rum-init). US1 (`datadoghq.com`) and EU serve the app under
 * `app.<site>`; regional sites (us3./us5./ap1.datadoghq.com) address it at the site host itself.
 */
const DATADOG_SITE = process.env.NEXT_PUBLIC_DATADOG_SITE ?? "datadoghq.com";

function datadogAppHost(site: string): string {
  return site === "datadoghq.com" || site === "datadoghq.eu" ? `app.${site}` : site;
}

function datadogUrlForQuery(query: string): string {
  return `https://${datadogAppHost(DATADOG_SITE)}/logs?query=${encodeURIComponent(query)}`;
}

/**
 * A deep link to this session's server-side logs in Datadog — all services, faceted by
 * `@context.session_id`, so a tester's Jam recording links to the full cross-service trace of the
 * session (BFF → api → adconnect → upstreams). Datadog applies its default time range; widen it
 * there when reviewing an older recording.
 */
export function datadogLogsUrl(sessionId: string): string {
  return datadogUrlForQuery(`@context.session_id:${sessionId}`);
}

/** A deep link to a *single request's* logs across all services (`@context.correlation_id`). */
export function datadogCorrelationUrl(correlationId: string): string {
  return datadogUrlForQuery(`@context.correlation_id:${correlationId}`);
}

export type BrowserLogMode = "off" | "ids" | "full";

const IS_PROD = process.env.NEXT_PUBLIC_WRANGO_PLATFORM_ENV === "prod";

/** Per-env browser logging mode (default from `logging-defaults.ts`). Env
 *  `NEXT_PUBLIC_WRANGO_BROWSER_LOG_MODE` overrides. */
export function browserLogMode(): BrowserLogMode {
  const value = process.env.NEXT_PUBLIC_WRANGO_BROWSER_LOG_MODE?.toLowerCase();
  if (value === "off" || value === "ids" || value === "full") return value;
  return loggingDefaults().browserMode;
}

export type BrowserLogScope = "api" | "all";

/**
 * Which requests to log. `api` = same-origin `/api/*` only (the app's tRPC +
 * route handlers). `all` = every browser fetch (RSC, SDKs, third-party) — opt-in
 * for deep local debugging; still fully redacted by the logger. Default from
 * `logging-defaults.ts`; env `NEXT_PUBLIC_WRANGO_BROWSER_LOG_SCOPE` overrides.
 */
export function browserLogScope(): BrowserLogScope {
  const value = process.env.NEXT_PUBLIC_WRANGO_BROWSER_LOG_SCOPE?.toLowerCase();
  if (value === "api" || value === "all") return value;
  return loggingDefaults().browserScope;
}

/**
 * Whether the redactor runs over browser log lines. Default from
 * `logging-defaults.ts` (on except local); env
 * `NEXT_PUBLIC_WRANGO_BROWSER_LOG_REDACT` overrides. The default browser scope
 * (`api`, same-origin `/api/*`) never logs headers/cookies, so the only value
 * that flows with redaction off in local is same-origin response bodies; flipping
 * scope to `all` with redaction off can surface third-party secrets in the console.
 */
export function browserRedactEnabled(): boolean {
  return envBool(process.env.NEXT_PUBLIC_WRANGO_BROWSER_LOG_REDACT, loggingDefaults().browserRedact);
}

/** Whether SPA page-view logging (`web.pageview`) is on. Default from
 *  `logging-defaults.ts`; env `NEXT_PUBLIC_WRANGO_BROWSER_PAGEVIEW` overrides. */
export function browserPageViewEnabled(): boolean {
  return envBool(process.env.NEXT_PUBLIC_WRANGO_BROWSER_PAGEVIEW, loggingDefaults().browserPageView);
}

/** The last correlation id seen on a response — for prod debug-id retrieval. */
let lastCorrelationId: string | undefined;

/**
 * Every distinct correlation id seen on this page (most-recent-last, capped). One correlation id
 * per inbound HTTP request — tRPC batches several procedure calls into one POST, so a session spans
 * several. Jam exposes no recording-start hook, so this accumulates from page load: a *superset* of
 * any single recording's window. The session-scoped `datadogUrl` remains the authoritative
 * "everything for this session, across every service" view.
 */
const MAX_TRACKED_CORRELATIONS = 50;
const seenCorrelationIds: string[] = [];

function trackCorrelationId(id: string): void {
  if (seenCorrelationIds.includes(id)) return;
  seenCorrelationIds.push(id);
  if (seenCorrelationIds.length > MAX_TRACKED_CORRELATIONS) seenCorrelationIds.shift();
}

/** Debug ids (+ ready Datadog links) for customer support. Prod-safe: does not depend on the console. */
export function getDebugInfo(): {
  correlationId: string | undefined;
  correlationIds: string[];
  sessionId: string;
  datadogUrl: string;
  datadogCorrelationUrls: string[];
} {
  const sessionId = getOrCreateSessionId();
  return {
    correlationId: lastCorrelationId,
    correlationIds: [...seenCorrelationIds],
    sessionId,
    datadogUrl: datadogLogsUrl(sessionId),
    datadogCorrelationUrls: seenCorrelationIds.map(datadogCorrelationUrl),
  };
}

// prod strips console.log; warn survives so the line is still Jam-capturable.
const browserSink: LogSink = (line) => (IS_PROD ? console.warn(line) : console.log(line));

/**
 * Two browser loggers under one service (`platform-web`), split by concern
 * so they toggle independently — facet by `@logger`:
 * - `web.fetch` — `browser_request`, per API call (governed by `browserLogMode`).
 * - `web.pageview` — `page_view`, per SPA navigation (governed by `browserPageViewEnabled`).
 * The ambient bound context is `session_id` only (stable); the per-request
 * `correlation_id` is pinned per line via `.with(...)` at the call site, so it is
 * correct even when a `full`-mode line is emitted after an async body read.
 */
function makeBrowserLogger(name: string): Logger {
  return createLogger({
    service: "platform-web",
    logger: name,
    getBoundContext: () => ({ session_id: getOrCreateSessionId() }),
    sink: browserSink,
    redact: browserRedactEnabled(),
  });
}

const fetchLogger = makeBrowserLogger("web.fetch");
const pageViewLogger = makeBrowserLogger("web.pageview");

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method;
  if (typeof input === "object" && input !== null && "method" in input) {
    return (input as Request).method;
  }
  return "GET";
}

function parseCapped(text: string | null): object | string | null {
  if (!text) return null;
  const capped = text.length > MAX_BODY_CHARS ? text.slice(0, MAX_BODY_CHARS) : text;
  try {
    return JSON.parse(capped) as object;
  } catch {
    return capped;
  }
}

/** Skip framework/telemetry noise so the logs stay useful. */
/**
 * Only log the app's own API calls — same-origin `/api/*` (tRPC + route
 * handlers). Excludes framework internals (RSC payloads, server actions,
 * `_next`), third-party SDKs (Clerk), telemetry, and other cross-origin
 * fetches, which are noise for debugging. Broaden here if a first-party
 * external call (e.g. an S3 upload) becomes worth logging.
 */
function shouldLog(url: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

function logFetch(
  mode: Exclude<BrowserLogMode, "off">,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  response: Response,
  correlationId: string | undefined,
): void {
  // Pin this request's correlation id (server-minted, per-request) under `context`.
  // Captured now so a `full`-mode line emitted after the async body read still
  // carries this request's id, not a since-updated `lastCorrelationId`.
  const log = correlationId ? fetchLogger.with({ correlation_id: correlationId }) : fetchLogger;
  const base = {
    method: resolveMethod(input, init),
    url: resolveUrl(input),
    status_code: response.status,
  };
  if (mode === "ids") {
    log.info("browser_request", base);
    return;
  }
  // full: include redacted bodies. Read the response off a clone so the stream
  // still flows to the caller; log once drained (fire-and-forget).
  const requestBody = typeof init?.body === "string" ? parseCapped(init.body) : null;
  const clone = response.clone();
  void clone
    .text()
    .catch(() => null)
    .then((text) => {
      log.info("browser_request", {
        ...base,
        request_body: requestBody,
        response_body: parseCapped(text),
      });
    });
}

/** Emit a `page_view` line per SPA navigation (toggle-gated, separate from fetch logging). */
export function logPageView(path: string): void {
  if (!browserPageViewEnabled()) return;
  const log = lastCorrelationId
    ? pageViewLogger.with({ correlation_id: lastCorrelationId })
    : pageViewLogger;
  log.info("page_view", { path });
}

let installed = false;

/**
 * Patch `window.fetch` once so every browser API call is logged through the one
 * path above, and the echoed `x-correlation-id` is captured for `getDebugInfo`.
 * Idempotent; safe to call from a client bootstrap effect.
 */
export function installBrowserFetchLogging(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Dev convenience: surface this session's Datadog link in the console once, so it's grabbable
  // outside Jam. Gated to browser-logging-enabled AND non-prod — prod's browser mode is `ids`
  // (not `off`), so the explicit IS_PROD guard is load-bearing: it keeps this out of prod consoles.
  if (!IS_PROD && browserLogMode() !== "off") {
    console.info(`[wrango] Datadog logs for this session → ${datadogLogsUrl(getOrCreateSessionId())}`);
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    const correlationId = response.headers.get(CORRELATION_ID_HEADER) ?? undefined;
    if (correlationId) {
      lastCorrelationId = correlationId;
      trackCorrelationId(correlationId);
    }
    const mode = browserLogMode();
    if (mode !== "off" && (browserLogScope() === "all" || shouldLog(resolveUrl(input)))) {
      logFetch(mode, input, init, response, correlationId);
    }
    return response;
  };
}
